import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Box } from '@mui/material';
import { useCustomerDetail } from '../../hooks/useCustomerDetail';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';
import CustomerHeader from './CustomerHeader';
import CustomerConfigurationTabs from './CustomerConfigurationTabs';
import CustomerToggle from './CustomerToggle';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorAlert from '../shared/ErrorAlert';

/**
 * CustomerDetailView - Display customer-specific configuration options
 * 
 * Shows customer information and provides tabbed navigation for:
 * - Rule Sets
 * - Metadata Fields
 * - Reference File Configuration
 * - Augmentation Rules
 * 
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6
 */
const CustomerDetailView = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, loading, error } = useCustomerDetail(customerId);

  // Register breadcrumbs - show "Loading..." until customer data is available
  const customerLabel = customer?.customerName || 'Loading...';
  usePageBreadcrumbs([
    { label: 'Customers', path: '/customers' },
    { label: customerLabel }
  ], [customerLabel]);
  
  // Check if we're returning from an augmentation page with a tab preference
  const initialTab = location.state?.returnTab || 'rulesets';
  const [activeTab, setActiveTab] = useState(initialTab);

  /**
   * Update active tab when location state changes
   */
  useEffect(() => {
    if (location.state?.returnTab) {
      setActiveTab(location.state.returnTab);
    }
  }, [location.state]);

  /**
   * Handle 404 errors - redirect to customer list
   */
  useEffect(() => {
    if (error && error.includes('not found')) {
      // Redirect to customer list after a brief delay
      const timer = setTimeout(() => {
        navigate('/customers');
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [error, navigate]);

  /**
   * Handle back navigation to customer list
   */
  const handleBack = () => {
    navigate('/customers');
  };

  /**
   * Handle tab change
   * @param {string} tab - The new active tab key
   */
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  /**
   * Handle error alert close
   */
  const handleErrorClose = () => {
    // Error will be cleared on next successful fetch
  };

  // Show loading spinner while fetching
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <LoadingSpinner size="large" />
      </Container>
    );
  }

  // Show error alert if error occurred
  if (error) {
    const isDeactivated = error.includes('not found');
    const displayMessage = isDeactivated
      ? 'This customer has been deactivated'
      : error;

    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <ErrorAlert 
          message={displayMessage} 
          onClose={handleErrorClose}
        />
        {isDeactivated && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            Redirecting to customer list...
          </Box>
        )}
      </Container>
    );
  }

  // Don't render if no customer data
  if (!customer) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Customer Header */}
      <CustomerHeader 
        customer={customer} 
        onBack={handleBack}
      />

      {/* Customer Toggle (provisioning enable/disable) */}
      <CustomerToggle customerId={customerId} />

      {/* Configuration Tabs */}
      <CustomerConfigurationTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        customerId={customerId}
      />
    </Container>
  );
};

export default CustomerDetailView;
